import React, { useState, useEffect } from 'react';
import './App.css';

// Screens
import WelcomeScreen from './components/screens/WelcomeScreen';
import CameraScreen from './components/screens/CameraScreen';
import StyleSelectionScreen from './components/screens/StyleSelectionScreen';
import LoadingScreen from './components/screens/LoadingScreen';
import ResultScreen from './components/screens/ResultScreen';
import QRCodeScreen from './components/screens/QRCodeScreen';
import ImageViewerScreen from './components/screens/ImageViewerScreen';

// Services
import { generateStyledImage } from './services/gemini';
import { saveGeneratedImage, uploadUserPhotoBackground } from './services/storage';

// Utils
import { SCREENS, UNIVERSAL_NEGATIVE_PROMPT, ENVIRONMENT_NEGATIVE, TRANSFORMATION_NEGATIVE, ACCURACY_REQUIREMENTS } from './utils/constants';
import { getImageIdFromUrl, upscaleImage, compressForUpload, applyStickerOverlay, applyImageBanners, BANNER_MODE } from './utils/imageUtils';
import { STICKER_IMAGES } from './components/shared/StickerSelector';

function App() {
  // Check if this is a mobile viewer request (via QR code)
  const imageIdFromUrl = getImageIdFromUrl();
  const isViewerMode = !!imageIdFromUrl;

  // App state
  const [currentScreen, setCurrentScreen] = useState(
    isViewerMode ? SCREENS.IMAGE_VIEWER : SCREENS.WELCOME
  );
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [compressedImage, setCompressedImage] = useState(null); // Compressed version for upload
  const [imageUrl, setImageUrl] = useState('');
  const [error, setError] = useState(null);
  const [loadingType, setLoadingType] = useState('generating'); // 'generating' or 'saving'
  const [userPhotoUpload, setUserPhotoUpload] = useState({ promise: null, url: null }); // Background upload tracking

  // Reset app to initial state
  const resetApp = () => {
    setCurrentScreen(SCREENS.WELCOME);
    setCapturedPhoto(null);
    setSelectedStyle(null);
    setGeneratedImage(null);
    setCompressedImage(null);
    setImageUrl('');
    setError(null);
    setUserPhotoUpload({ promise: null, url: null });
  };

  // Navigation handlers
  const handleStartClick = () => {
    setCurrentScreen(SCREENS.CAMERA);
  };

  const handlePhotoCapture = (photo) => {
    setCapturedPhoto(photo);
    setCurrentScreen(SCREENS.STYLE_SELECTION);

    // Start background upload while user selects style (optimization)
    console.log('[APP] Starting background upload of user photo...');
    const uploadPromise = uploadUserPhotoBackground(photo).then(url => {
      if (url) {
        setUserPhotoUpload(prev => ({ ...prev, url }));
        console.log('[APP] Background upload complete:', url);
      }
      return url;
    });
    setUserPhotoUpload({ promise: uploadPromise, url: null });
  };

  const handleStyleSelect = async (style) => {
    setSelectedStyle(style);
    setLoadingType('generating');
    setCurrentScreen(SCREENS.LOADING);

    try {
      // Adjust prompt based on generation mode
      let finalPrompt = style.prompt;

      if (style.generationMode === 'environment') {
        // Environment Only: Composite user into reference environment image
        // Sends user photo + high-quality reference environment image
        finalPrompt = `PHOTOREALISTIC BACKGROUND REPLACEMENT

*** MANDATORY - THE PERSON MUST ALWAYS APPEAR IN THE OUTPUT IMAGE ***
*** THIS IS NON-NEGOTIABLE - NEVER GENERATE AN IMAGE WITHOUT THE PERSON ***

INPUT IMAGES:
- Image 1 (User Photo): THE PERSON - MUST APPEAR IN OUTPUT
- Image 2 (Reference Environment): Background style reference only

=== ABSOLUTE REQUIREMENTS (FAILURE TO FOLLOW = INVALID OUTPUT) ===

REQUIREMENT #1 - PERSON MUST BE VISIBLE:
- The person from Image 1 MUST appear in the final generated image
- NEVER remove the person from the image
- NEVER generate only the background without the person
- The person is THE MAIN SUBJECT - more important than the background
- If you cannot include the person, DO NOT generate the image

REQUIREMENT #2 - PERSON MUST BE COMPLETE:
- Show the person's COMPLETE head, face, neck, and upper body as in Image 1
- NEVER cut off body parts or crop the person incorrectly
- NEVER put holes, gaps, or transparency through the person's body
- NEVER let background elements show through the person
- The person must be SOLID and COMPLETE - no missing parts

REQUIREMENT #3 - PERSON MUST BE UNCHANGED:
- Same face, same expression, same pose, same clothing
- Same skin tone, same hair, same body proportions
- DO NOT alter, transform, stylize, or modify the person
- The person must remain PHOTOREALISTIC

REQUIREMENT #4 - MAINTAIN EXACT POSITION AND FRAMING:
- Keep the person at the EXACT SAME SIZE as Image 1
- CRITICAL: Maintain the EXACT SAME POSITION in the frame:
  * If the person touches the BOTTOM edge in Image 1, they MUST touch the BOTTOM edge in output
  * If the person touches the LEFT or RIGHT edges in Image 1, they MUST touch those edges in output
  * If the person fills the frame edge-to-edge, they MUST fill edge-to-edge in output
- DO NOT float the person in the middle with empty space around them
- DO NOT add extra space above the head or below the body
- DO NOT shrink the person to fit more background
- The person's position relative to frame edges must be IDENTICAL to Image 1
- Portrait 2:3 ratio output

TASK:
1. EXTRACT the person from Image 1 completely
2. REPLACE the background with environment inspired by Image 2
3. KEEP the person in their EXACT original position (same edges they touch)
4. The background goes BEHIND - person stays in SAME position
5. Add appropriate lighting but DO NOT move or resize the person

BACKGROUND ENVIRONMENT (from Image 2):
${style.prompt}

OUTPUT: The EXACT same framing as Image 1 - person in SAME position touching SAME edges - with ONLY the background replaced. DO NOT reposition or float the person in the middle.

NEGATIVE (avoid these): ${UNIVERSAL_NEGATIVE_PROMPT}, ${ENVIRONMENT_NEGATIVE}`;
      } else if (style.generationMode === 'transformation') {
        // Full Transformation: PHOTOREALISTIC transformation with UAE cultural clothing
        finalPrompt = `PHOTOREALISTIC IDENTITY-PRESERVING TRANSFORMATION

*** CRITICAL: PRESERVE THE PERSON'S EXACT FACE AND IDENTITY ***
The person's face, facial features, skin tone, and identity from the User Photo MUST be preserved exactly.
They must be clearly recognizable as the same person.

*** PHOTOREALISTIC OUTPUT REQUIRED ***
This MUST look like a real photograph - NOT a painting, illustration, cartoon, or digital art.
Use realistic skin texture with visible pores, natural lighting, and genuine photographic quality.

GENDER-ADAPTIVE UAE CULTURAL CLOTHING:
- For WOMEN: Elegant abaya (black or colored), hijab if the person wears one in the original photo, or modest professional attire appropriate for UAE culture
- For MEN: Traditional white kandura (thobe) with ghutra/agal headwear, OR formal UAE business attire

TRANSFORMATION INSTRUCTIONS:
${style.prompt}

QUALITY REQUIREMENTS:
- Shot on professional camera, sharp focus
- Natural skin texture visible (pores, subtle imperfections)
- Realistic lighting matching the environment
- 2:3 vertical portrait format
- The person must look like they were actually photographed in this setting

DO NOT: Change the person's face, alter their identity, create cartoon/illustrated look, use smooth/airbrushed skin, generate a different person.

${ACCURACY_REQUIREMENTS}

NEGATIVE (avoid these): ${UNIVERSAL_NEGATIVE_PROMPT}, ${TRANSFORMATION_NEGATIVE}`;
      }

      console.log('Generating styled image with:', style.name);
      console.log('Generation mode:', style.generationMode);
      console.log('Final prompt:', finalPrompt);

      // Use reference image only for environment mode
      // Transformation mode: No reference image needed - prompt contains all instructions
      const referenceImageToUse = style.generationMode === 'environment'
        ? style.referenceImage
        : null;

      console.log('Reference image:', referenceImageToUse || 'None (transformation mode)');

      // Wait for background upload to complete if still in progress
      let userPhotoUrl = userPhotoUpload.url;
      if (!userPhotoUrl && userPhotoUpload.promise) {
        console.log('[APP] Waiting for background upload to complete...');
        userPhotoUrl = await userPhotoUpload.promise;
      }

      // Use URL if available (faster), otherwise fallback to base64
      const userPhotoInput = userPhotoUrl || capturedPhoto;
      const isUserPhotoUrl = !!userPhotoUrl;
      console.log('[APP] User photo:', isUserPhotoUrl ? 'Using URL' : 'Using base64 (fallback)');

      const result = await generateStyledImage(
        userPhotoInput,
        finalPrompt,
        referenceImageToUse,
        style.generationMode,
        isUserPhotoUrl // Flag to indicate if userPhoto is a URL
      );

      if (result.isAlreadySaved) {
        // OPTIMIZED FLOW: Image already saved to Cloudflare by n8n
        // Show image without banners in preview - banners will be applied when saving
        console.log('[APP] Image already saved to Cloudflare, showing preview...');
        setGeneratedImage(result.imageUrl); // Store URL for display (without banners in preview)
        setImageUrl(result.imageUrl); // Keep original URL
        setCompressedImage(null); // Not needed
        setCurrentScreen(SCREENS.RESULT);
      } else {
        // FALLBACK FLOW: Base64 response, process and save later
        console.log('[APP] Received base64, will save on confirmation');
        const styledImage = result.imageData;

        // Upscale generated image to high resolution (2560x3840) for better quality
        console.log('Upscaling generated image to high resolution...');
        const upscaledImage = await upscaleImage(styledImage, 2560, 3840);

        // Apply branding banners (top: Dubai Police logo, bottom: Speak Out)
        console.log('[APP] Applying branding banners...');
        const imageWithBanners = await applyImageBanners(upscaledImage, BANNER_MODE.EXTENSION);
        // ALTERNATIVE: To overlay instead of extend, use BANNER_MODE.OVERLAY
        console.log('[APP] Banners applied successfully');

        // Create compressed version for upload
        console.log('Creating compressed version for upload...');
        const compressed = await compressForUpload(imageWithBanners, 1920, 0.85);

        setGeneratedImage(imageWithBanners);
        setCompressedImage(compressed); // Store compressed version for later upload
        setCurrentScreen(SCREENS.RESULT);
      }
    } catch (err) {
      console.error('Error generating image:', err);
      setError(err.message);
      // Fallback: show original photo with error message
      setGeneratedImage(capturedPhoto);
      setCurrentScreen(SCREENS.RESULT);
    }
  };

  const handleRetake = () => {
    setCurrentScreen(SCREENS.CAMERA);
    setCapturedPhoto(null);
    setSelectedStyle(null);
    setGeneratedImage(null);
    setCompressedImage(null);
    setUserPhotoUpload({ promise: null, url: null });
  };

  const handleNext = async (selectedSticker = null) => {
    setError(null); // Clear any previous errors

    // Always save the image (with or without sticker)
    setLoadingType('saving');
    setCurrentScreen(SCREENS.LOADING);

    try {
      let imageToSave = compressedImage || generatedImage;

      // Check if imageToSave is a URL (optimized flow) or base64 (fallback flow)
      const isUrl = typeof imageToSave === 'string' && (imageToSave.startsWith('http://') || imageToSave.startsWith('https://'));

      // For optimized flow (URL): Banners and stickers can't be applied due to CORS
      // For fallback flow (base64): Banners already applied, only stickers can be added
      if (selectedSticker && !isUrl) {
        console.log('[APP] Applying sticker:', selectedSticker.name);
        const stickerSrc = STICKER_IMAGES[selectedSticker.id];
        imageToSave = await applyStickerOverlay(imageToSave, selectedSticker, stickerSrc);
        console.log('[APP] Sticker applied successfully');
      } else if (selectedSticker && isUrl) {
        console.log('[APP] Cannot apply sticker to URL-based image (CORS restriction)');
      }

      // Save image to R2 via n8n
      console.log('Saving image to R2...');
      const { imageId, imageUrl: savedUrl } = await saveGeneratedImage(imageToSave, selectedStyle.id);

      // Use the URL returned directly from n8n workflow
      setImageUrl(savedUrl);

      console.log('Image saved with ID:', imageId);
      console.log('Image URL for QR code:', savedUrl);
      setCurrentScreen(SCREENS.QR_CODE);
    } catch (err) {
      console.error('Error saving image:', err);
      // Use the specific error message from storage.js for better user feedback
      setError(err.message || 'Failed to generate QR code. Please try again.');
      // Stay on result screen or show error
      setCurrentScreen(SCREENS.RESULT);
    }
  };

  const handleDone = () => {
    resetApp();
  };

  const handleAutoReset = () => {
    console.log('Auto-reset triggered');
    resetApp();
  };

  // Disable right-click context menu for kiosk mode
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  // Disable keyboard shortcuts for kiosk mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Disable F11, F5, Ctrl+R, etc.
      if (
        e.key === 'F11' ||
        e.key === 'F5' ||
        (e.ctrlKey && e.key === 'r') ||
        (e.ctrlKey && e.key === 'w') ||
        (e.altKey && e.key === 'F4')
      ) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Render appropriate screen
  if (isViewerMode) {
    return <ImageViewerScreen imageId={imageIdFromUrl} />;
  }

  return (
    <div className="App">
      {currentScreen === SCREENS.WELCOME && (
        <WelcomeScreen onStartClick={handleStartClick} />
      )}

      {currentScreen === SCREENS.CAMERA && (
        <CameraScreen
          onPhotoCapture={handlePhotoCapture}
          onBack={resetApp}
          orientation="portrait"
        />
      )}

      {currentScreen === SCREENS.STYLE_SELECTION && (
        <StyleSelectionScreen
          capturedPhoto={capturedPhoto}
          onStyleSelect={handleStyleSelect}
          onBack={resetApp}
        />
      )}

      {currentScreen === SCREENS.LOADING && <LoadingScreen type={loadingType} />}

      {currentScreen === SCREENS.RESULT && (
        <ResultScreen
          generatedImage={generatedImage}
          styleName={selectedStyle?.name || 'Unknown'}
          onRetake={handleRetake}
          onNext={handleNext}
        />
      )}

      {currentScreen === SCREENS.QR_CODE && (
        <QRCodeScreen
          imageUrl={imageUrl}
          onDone={handleDone}
          onAutoReset={handleAutoReset}
        />
      )}

      {/* Error Display (if any) */}
      {error && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-red-500/90 backdrop-blur-sm px-6 py-3 rounded-full shadow-xl z-50">
          <p className="text-white font-semibold">{error}</p>
        </div>
      )}
    </div>
  );
}

export default App;
